import DOMPurify from 'dompurify';
import { marked } from 'marked';

export type ChatLine = {
    role: 'user' | 'assistant';
    text: string;
};

export type ThinkingSegments = {
    thinking: string;
    response: string;
};

export type SplitThinkingOptions = {
    /**
     * When true, incomplete tag prefixes at the end of the input are held out of
     * both sections until the tag completes. When false (finalized messages),
     * leftover incomplete prefixes are treated as response (or thinking, if
     * already inside an open thinking block).
     */
    streaming?: boolean;
};

type ThreadMessage = {
    role: string;
    content: { text?: string }[];
};

const OPEN_TAG = /^<thinking\s*>/i;
const SELF_CLOSING_TAG = /^<thinking\s*\/>/i;
const CLOSE_TAG = /^<\/thinking\s*>/i;

marked.setOptions({
    gfm: true,
    breaks: false,
});

export function threadMessagesToChatLines(messages: ThreadMessage[]): ChatLine[] {
    return messages
        .filter((message) => message.role === 'user' || message.role === 'assistant')
        .map((message) => ({
            role: message.role as 'user' | 'assistant',
            text:
                typeof message.content === 'string'
                    ? message.content
                    : message.content
                          .map((block) => (typeof block.text === 'string' ? block.text : ''))
                          .join(''),
        }))
        .filter((line) => line.text.length > 0);
}

export function formatToolLabel(toolName: string): string {
    switch (toolName) {
        case 'getReadonlyGraphqlSchema':
            return 'Loading GraphQL schema';
        case 'executeReadonlyGraphql':
            return 'Querying ACM data';
        case 'suggestNavigation':
            return 'Finding navigation';
        case 'searchKnowledgeBase':
            return 'Searching Knowledgebase';
        case 'activateSkill':
            return 'Using Skill';
        case 'readSkillReference':
            return 'Reading References';
        case 'searchSkillReferences':
            return 'Searching References';
        default:
            return toolName;
    }
}

/**
 * Split assistant raw text into thinking vs user-facing response segments.
 * Parses paired `<thinking>...</thinking>` tags (case-insensitive).
 * Self-closing `<thinking/>` alone produces no thinking body.
 */
export function splitThinkingSegments(
    raw: string,
    options?: SplitThinkingOptions
): ThinkingSegments {
    const streaming = options?.streaming === true;
    let thinking = '';
    let response = '';
    let index = 0;
    let inThinking = false;

    while (index < raw.length) {
        const lt = raw.indexOf('<', index);
        if (lt === -1) {
            const rest = raw.slice(index);
            if (inThinking) {
                thinking += rest;
            } else {
                response += rest;
            }
            break;
        }

        if (inThinking) {
            thinking += raw.slice(index, lt);
        } else {
            response += raw.slice(index, lt);
        }

        const fromLt = raw.slice(lt);

        if (!inThinking) {
            const selfClosing = fromLt.match(SELF_CLOSING_TAG);
            if (selfClosing) {
                index = lt + selfClosing[0].length;
                continue;
            }

            const open = fromLt.match(OPEN_TAG);
            if (open) {
                inThinking = true;
                index = lt + open[0].length;
                continue;
            }

            if (isIncompleteOpenTagPrefix(fromLt)) {
                if (streaming) {
                    break;
                }
                response += fromLt;
                break;
            }

            response += '<';
            index = lt + 1;
            continue;
        }

        const close = fromLt.match(CLOSE_TAG);
        if (close) {
            inThinking = false;
            index = lt + close[0].length;
            continue;
        }

        if (isIncompleteCloseTagPrefix(fromLt)) {
            if (streaming) {
                break;
            }
            thinking += fromLt;
            break;
        }

        thinking += '<';
        index = lt + 1;
    }

    return { thinking, response };
}

/**
 * Convert markdown to sanitized HTML for `[innerHTML]` binding.
 * marked output is always passed through DOMPurify before return.
 */
export function markdownToSafeHtml(markdown: string): string {
    if (markdown.length === 0) {
        return '';
    }
    const html = marked.parse(markdown, { async: false }) as string;
    return DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
}

function isIncompleteOpenTagPrefix(value: string): boolean {
    if (OPEN_TAG.test(value) || SELF_CLOSING_TAG.test(value)) {
        return false;
    }
    const lower = value.toLowerCase();
    if (!lower.startsWith('<')) {
        return false;
    }
    // Incomplete prefixes of `<thinking>` / `<thinking/>` / `<thinking />`
    const openStem = '<thinking';
    if (openStem.startsWith(lower) || lower === openStem) {
        return true;
    }
    if (!lower.startsWith(openStem)) {
        return false;
    }
    const after = lower.slice(openStem.length);
    return /^\s*\/?$/.test(after);
}

function isIncompleteCloseTagPrefix(value: string): boolean {
    if (CLOSE_TAG.test(value)) {
        return false;
    }
    const lower = value.toLowerCase();
    if (!lower.startsWith('<')) {
        return false;
    }
    const closeStem = '</thinking';
    if (closeStem.startsWith(lower) || lower === closeStem) {
        return true;
    }
    if (!lower.startsWith(closeStem)) {
        return false;
    }
    return /^\s*$/.test(lower.slice(closeStem.length));
}
